import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useStore } from '../../store';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { createLocalRecord, db, withExtras, withFormulas, withParents } from '../../Utils/Functions/Dexie-Setup';
import { useLiveQuery } from 'dexie-react-hooks';
import SRFButton from '../Inputs/SRFButton/SRFButton';
import DateInput from '../Inputs/Date-Input';
import RelationshipInput from '../Inputs/RelationshipInput';
import Loader from '../elements/Loader/Loader';
import TextInput from '../Inputs/Text-Input';
import TextMultiLineInput from '../Inputs/Text-Multi-line-Input';
import SignatureInput from '../Inputs/SignatureInput';
import DataSheetInput from '../Inputs/DatasheetInput';
import { createAddRowsComponent } from 'react-datasheet-grid';
import { duplicateDFRDetails } from '../../Utils/Functions/helpers';
import { checkLocationPermission } from '../../Utils/Functions/locationUtils';
import { showErrorToast } from '../../Utils/Functions/toastService';

const DeleteColumn = {
  component: ({ deleteRow }) => (
    <button type='button' className='text-center w-full h-full' onClick={deleteRow}>❌</button>
  ),
  basis: 50,
}

function Form() {
  const { isAuthed, translation } = useStore();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const createdRid = useRef(false);
  const laborRef = useRef(null);
  const equipmentRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const AddRows = useMemo(() => createAddRowsComponent({
    button: translation.add,
    unit: translation.rows,
  }), [translation]);

  const key = useMemo(() => Number(params?.key) || Number(atob(params?.key || "") ?? 0), [params]);

  const [currentSubmission, loaded] = useLiveQuery(() =>
    db.dailyFieldReports.get(key)
      .then(record => record ? withExtras("dailyFieldReports", [withParents, withFormulas])([record]) : undefined)
      .then(res => res ? res[0] : undefined)
      .then(res => [res, true]), [key], [undefined, false]);


  const laborItems = useLiveQuery(() =>
    db.laborLineItems.where("relatedDailyFieldReport").equals(key).toArray()
  , [key], []);

  const equipmentItems = useLiveQuery(() =>
    db.equipmentLineItems.where("relatedDailyFieldReport").equals(key).toArray()
  , [key], []);

  const previousDFRForJob = useLiveQuery(() => 
    db.dailyFieldReports
      .where("jobNumber")
      .equals(currentSubmission?.jobNumber || "null")
      .and(dfr => dfr.recordId !== key && dfr.recordId > 0)
      .reverse()
      // .orderBy("dateCreated")
      .first()
  , [currentSubmission, key], []);


  const submitRecord = useCallback(async (e) => {
    e.preventDefault();
  
    setIsSubmitting(true);

    try {
      await Promise.all([
        laborRef.current?.forceSave(),
        equipmentRef.current?.forceSave(),
      ]);
      
      // Quick check for location permission (uses cached location)
      try {
        await checkLocationPermission();
      } catch (error) {
        setIsSubmitting(false);
        showErrorToast("Location access is required to submit a Daily Field Report. " + error.message);
        
        // Try to request location permission again
        try {
          await checkLocationPermission();
        } catch (retryError) {
          // User still denied or location still unavailable, stop here
          return;
        }
      }

      const nextSubmitted = !currentSubmission?.submitted;
      
      // Mark as submitted (location will be stamped during backend sync)
      db.dailyFieldReports.update(key, { 
        submitted: nextSubmitted, 
        locallyUpdatedAt: Date.now()
      });
      
      if (nextSubmitted) {
        navigate("/");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [currentSubmission, key, navigate]);

  const laborFilter = useCallback((laborItem) => laborItem.relatedDailyFieldReport === key, [key]);
  const laborDefault = useMemo(() => ({
    relatedDailyFieldReport: key,
  }), [key]);

  const laborColumns = useMemo(() => [
    ["relatedEmployee", { title: translation.employee, disabled: key > 0, displayFields: ["name"], isCellEmpty: () => true, required: true }],
    ["relatedEmployeeTitles", { title: translation.position, disabled: key > 0, isCellEmpty: ({ rowData }) => rowData }],
    ["hours", { title: translation.hours, disabled: key > 0, isCellEmpty: () => true, required: true }],
  ], [translation, key]);

  const equipmentFilter = useCallback((equipment) => equipment.relatedDailyFieldReport === key, [key]);

  const equipmentDefault = useMemo(() => ({ relatedDailyFieldReport: key }), [key]);

  const equipmentColumns = useMemo(() => [
    ["typeOfEquipment", { title: translation.typeOfEquipment, disabled: key > 0, isCellEmpty: () => true }],
    ["serialNumber", { title: translation.serialNumber, disabled: key > 0, isCellEmpty: () => true }]
  ], [translation, key]);

  useEffect(() => {
    if (!isAuthed) {
      navigate("/auth");
    }
  }, [isAuthed, navigate]);

  useEffect(() => {
    console.log({laborItems, equipmentItems, previousDFRForJob});
  }, [laborItems, equipmentItems, previousDFRForJob]);
  
  useEffect(() => {
    if ((key && loaded) && !currentSubmission) {
      const timeout = setTimeout(() => navigate("/", { replace: true }), 4000);
      return () => clearTimeout(timeout);
    }
  }, [key, loaded, currentSubmission, navigate]);

  useEffect(() => {
    if (!key && !createdRid.current) {
      const defaultValues = {
        date: new Date().toISOString().split('T')[0],
        status: "Submitted"
      };
      createdRid.current = true;
      searchParams.forEach((value, key) => defaultValues[key] = value);
      (async () => {
        const newKey = await createLocalRecord("dailyFieldReports", defaultValues);


        navigate(`/form/${btoa(newKey)}`, { replace: true });
      })();
    }
  }, [key, navigate, searchParams]);

  useEffect(() => {

  }, []);

  if (currentSubmission) {
    return (
      <form className='flex flex-col items-center py-4 gap-6' onSubmit={submitRecord}>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <Link to="/">
            <SRFButton color='#0f314d'>{translation.home}</SRFButton>
          </Link>
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <DateInput
            required
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"date"}
            label={translation.date}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <RelationshipInput
            required
            disabled={key > 0}
            tableName={"jobs"}
            fieldValue={currentSubmission?.jobNumber ?? null}
            setFieldValue={(jobNumber) => db.dailyFieldReports.update(key, { jobNumber: jobNumber })}
            fieldName={"jobNumber"}
            label={translation.job}
            displayFields={["jobNumber", "jobName"]}
          />
        </div>
        { previousDFRForJob && !laborItems.length && !equipmentItems.length ?
            <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
              <SRFButton color='#8fc13b' onClick={() => duplicateDFRDetails(previousDFRForJob.recordId, key) }>
                {translation.useLineItemsFromLastDFROnJob}
              </SRFButton>
            </div> : null  
      }
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextMultiLineInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"notes"}
            label={translation.summaryOfWorkCompleted}
            resizeHint={translation.dragCornerToResize}
          />
        </div>
        <h3 className='text-lg'>{translation.laborAndEquipment}</h3>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            ref={laborRef}
            tableName={"laborLineItems"}
            filter={laborFilter}
            defaultValues={laborDefault}
            columns={laborColumns}
            label={translation.laborLineItems}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            ref={equipmentRef}
            tableName={"equipmentLineItems"}
            filter={equipmentFilter}
            defaultValues={equipmentDefault}
            columns={equipmentColumns}
            label={translation.additionalEquipment}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <h3 className='text-lg'>{translation.signOff}</h3>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"foremanName"}
            label={translation.foremanName}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <SignatureInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"foremanSignature"}
            label={translation.foremanSignature}
            buttonLabels={translation}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"inspectorName"}
            label={translation.inspectorName}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <SignatureInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"inspectorSignature"}
            label={translation.inspectorSignature}
            buttonLabels={translation}
          />
        </div>
        {
          key < 0 ?
            <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
              <SRFButton type={"submit"} color='#0f314d' disabled={isSubmitting}>
                {isSubmitting ? "Checking for permissions..." : (currentSubmission?.submitted ? translation.unsubmit : translation.submit)}
              </SRFButton>
            </div>
            : null
        }
      </form>
    )
  } else {
    return <Loader />
  }

}

export default Form;
